import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { PortalAlerts } from "../components/PortalAlerts";
import { api } from "../lib/api";
import { Modal } from "../components/Modal";
import { useResource, formatDate, formatDateTime, daysUntil } from "../lib/hooks";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { ANNOUNCEMENT_CHANNELS } from "../lib/formOptions";
import type {
    Announcement, CompliancePerson, Dashboard as DashboardData, PersonAnniversary,
} from "../lib/types";

const Stat = ({ label, value, sub }: { label: string; value: string | number; sub?: string }) => (
    <div className="card stat">
        <div className="label">{label}</div>
        <div className="value">{value}</div>
        {sub && <div className="sub">{sub}</div>}
    </div>
);

const meterColour = (percent: number): string =>
    percent >= 90 ? "var(--pass)" : percent >= 60 ? "var(--warn)" : "var(--block)";

const ComplianceList = ({ title, people }: { title: string; people: CompliancePerson[] }) => (
    <div className="card">
        <div className="card-head"><h2>{title}</h2></div>
        <div className="card-body">
            {people.length === 0 ? (
                <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>Nothing to show.</p>
            ) : people.map((person) => (
                <div key={person.id} className="person-row">
                    <InitialsAvatar name={person.name} />
                    <div className="person-main">
                        <div className="name">{person.name}</div>
                        {person.required !== undefined && (
                            <div className="sub">
                                {person.valid} of {person.required} required competencies current
                            </div>
                        )}
                    </div>
                    <div className="meter" style={{ width: 120 }}>
                        <div className="meter-track">
                            <div className="meter-fill"
                                style={{ width: `${person.percent}%`, background: meterColour(person.percent) }} />
                        </div>
                        <span className="meter-value">{person.percent}%</span>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const PeoplePanel = ({
    title, people, icon, describe, onWish, wishSent,
}: {
    title: string;
    people: PersonAnniversary[];
    icon: "gift" | "medal";
    describe: (person: PersonAnniversary) => string;
    /** When present, each row offers a "Send wishes" action. */
    onWish?: (person: PersonAnniversary) => void;
    wishSent?: (person: PersonAnniversary) => boolean;
}) => (
    <div className="card">
        <div className="card-head">
            <h2>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                     style={{ marginRight: 6, verticalAlign: "-2px", color: "var(--brand-accent)" }}>
                    {icon === "gift" ? (
                        <>
                            <rect x="3" y="8" width="18" height="4" />
                            <path d="M5 12v9h14v-9" /><path d="M12 8v13" />
                            <path d="M12 8c-3 0-4.5-1.2-4.5-2.7C7.5 3.9 8.6 3 9.8 3 11.5 3 12 5 12 8Z" />
                            <path d="M12 8c3 0 4.5-1.2 4.5-2.7C16.5 3.9 15.4 3 14.2 3 12.5 3 12 5 12 8Z" />
                        </>
                    ) : (
                        <>
                            <circle cx="12" cy="15" r="5" />
                            <path d="m9 10.5-4-7.5h5l2 3.5L14 3h5l-4 7.5" />
                        </>
                    )}
                </svg>
                {title}
            </h2>
            <span className="chip mute">next 30 days</span>
        </div>
        <div className="card-body">
            {people.length === 0 ? (
                <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>None in the next 30 days.</p>
            ) : people.map((person) => {
                const days = daysUntil(person.next_date);
                return (
                    <div key={`${person.id}-${person.next_date}`} className="person-row">
                        <InitialsAvatar name={person.name} />
                        <div className="person-main">
                            <div className="name">
                                {person.name}
                                {person.person_kind && (
                                    <span className="chip mute" style={{ marginLeft: 6 }}>{person.person_kind}</span>
                                )}
                            </div>
                            <div className="sub">{describe(person)}</div>
                        </div>
                        <span className={`chip ${days === 0 ? "pass" : "mute"}`}>
                            {days === 0 ? "today" : days === 1 ? "tomorrow" : `${days} days`}
                        </span>
                        {onWish && (
                            wishSent?.(person) ? (
                                <span className="chip pass" title="You have sent your wishes this year">sent</span>
                            ) : (
                                <button className="icon-btn" onClick={() => onWish(person)}>
                                    Send wishes
                                </button>
                            )
                        )}
                    </div>
                );
            })}
        </div>
    </div>
);

interface SentWish { recipient_kind: string; recipient_id: string; }

/**
 * Small compose dialog for a birthday wish. One per sender per person per
 * year -- the database enforces that, so a double-send is a friendly no-op.
 */
const WishDialog = ({ person, onClose, onSent }: {
    person: PersonAnniversary;
    onClose: () => void;
    onSent: () => void;
}) => {
    const [message, setMessage] = useState(
        `Happy birthday, ${person.name.split(" ")[0]}! Hope you have a wonderful day.`
    );
    const [busy, setBusy] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);

    const send = async () => {
        setBusy(true);
        setSendError(null);
        try {
            await api.post("/birthday-wishes", {
                recipientKind: person.person_kind ?? "employee",
                recipientId: person.id,
                message: message.trim(),
            });
            onSent();
        } catch (err) {
            setSendError(err instanceof Error ? err.message : "Could not send");
            setBusy(false);
        }
    };

    return (
        <Modal title={`Birthday wishes — ${person.name}`} onClose={onClose}
            footer={<>
                <button className="btn" onClick={() => void send()} disabled={busy || message.trim().length === 0}>
                    {busy ? "Sending…" : "Send wishes"}
                </button>
                <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
            </>}>
            {sendError && <div className="error-banner">{sendError}</div>}
            <div className="field">
                <label htmlFor="wish-msg">Your message</label>
                <textarea id="wish-msg" value={message} maxLength={500}
                    onChange={(e) => setMessage(e.target.value)} />
                <span className="help">
                    {person.person_kind === "participant"
                        ? "Participants do not sign in — their wishes are shown to the team supporting them, to pass on in person."
                        : "Appears in their portal when they next sign in."}
                </span>
            </div>
        </Modal>
    );
};

const Feeds = ({ items }: { items: Announcement[] }) => {
    const [channel, setChannel] = useState<Announcement["channel"]>("system_news");
    const active = ANNOUNCEMENT_CHANNELS.find((c) => c.value === channel)!;
    const shown = items.filter((item) => item.channel === channel);

    return (
        <div className="card">
            <div className="card-head" style={{ display: "block" }}>
                <h2 style={{ marginBottom: "0.6rem" }}>Feeds</h2>
                <div className="feed-tabs" style={{ flexWrap: "wrap" }}>
                    {ANNOUNCEMENT_CHANNELS.map((c) => (
                        <button key={c.value}
                            className={`feed-tab ${channel === c.value ? "on" : ""}`}
                            onClick={() => setChannel(c.value)}>
                            {c.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="card-body">
                {shown.length === 0 ? (
                    <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>Nothing posted in this channel yet.</p>
                ) : shown.map((item) => (
                    <article key={item.id} className="feed-item">
                        <h4>{item.title}</h4>
                        <p>{item.body}</p>
                        <time dateTime={item.published_at}>{formatDate(item.published_at)}</time>
                        {item.link_url && (
                            <a href={item.link_url} target="_blank" rel="noreferrer"
                               style={{ fontSize: "0.76rem" }}>Read at source</a>
                        )}
                    </article>
                ))}
                {active.sourceUrl && (
                    <p style={{ marginTop: "0.6rem", fontSize: "0.76rem" }}>
                        <a href={active.sourceUrl} target="_blank" rel="noreferrer">
                            More from {active.sourceLabel}
                        </a>
                    </p>
                )}
            </div>
        </div>
    );
};

export const Dashboard = () => {
    const { user } = useAuth();
    const { data, error, loading } = useResource<DashboardData>("/dashboard");
    const sentWishes = useResource<SentWish[]>("/birthday-wishes/sent");
    const [wishTarget, setWishTarget] = useState<PersonAnniversary | null>(null);

    if (loading) return <p style={{ color: "var(--muted)" }}>Loading…</p>;
    if (error) return <div className="error-banner">{error}</div>;
    if (!data) return null;

    const alreadyWished = (person: PersonAnniversary) =>
        (sentWishes.data ?? []).some((w) =>
            w.recipient_kind === (person.person_kind ?? "employee") && w.recipient_id === person.id);


    return (
        <div style={{ display: "grid", gap: "1.25rem" }}>
            <PortalAlerts />
            <div className="grid cols-4">
                <Stat label="Active staff" value={data.counts.active_staff} />
                <Stat label="Active participants" value={data.counts.active_participants} />
                <Stat label="Open incidents" value={data.counts.open_incidents} />
                <Stat label="New referrals" value={data.counts.new_referrals} />
            </div>

            {(user?.role === "admin" || user?.role === "manager") && data.counts.documents_expired !== undefined && (
                <Link to="/compliance" className="card" style={{ display: "block", textDecoration: "none", color: "inherit" }}>
                    <div className="card-body" style={{ display: "flex", alignItems: "center", gap: "1.25rem", flexWrap: "wrap" }}>
                        <div>
                            <div className="num" style={{ fontSize: "1.6rem", fontWeight: 700, color: Number(data.counts.documents_expired) ? "var(--block)" : "var(--muted)" }}>
                                {data.counts.documents_expired} expired
                            </div>
                            <div className="lbl" style={{ color: "var(--muted)", fontSize: "0.78rem" }}>Documents past their expiry date</div>
                        </div>
                        <div>
                            <div className="num" style={{ fontSize: "1.6rem", fontWeight: 700, color: Number(data.counts.documents_expiring) ? "var(--warn)" : "var(--muted)" }}>
                                {data.counts.documents_expiring} expiring
                            </div>
                            <div className="lbl" style={{ color: "var(--muted)", fontSize: "0.78rem" }}>Within the warning window</div>
                        </div>
                        <div>
                            <div className="num" style={{ fontSize: "1.6rem", fontWeight: 700, color: Number(data.counts.documents_pending_verification) ? "var(--warn)" : "var(--muted)" }}>
                                {data.counts.documents_pending_verification} pending
                            </div>
                            <div className="lbl" style={{ color: "var(--muted)", fontSize: "0.78rem" }}>Awaiting admin verification</div>
                        </div>
                        <span className="spacer" />
                        <span className="chip info">View document expiry →</span>
                    </div>
                </Link>
            )}

            <div className="grid cols-2">
                <div className="card">
                    <div className="card-head">
                        <h2>Workforce compliance</h2>
                        <span className="chip info">derived, not stored</span>
                    </div>
                    <div className="card-body">
                        <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem" }}>
                            <span style={{ fontSize: "2.1rem", fontWeight: 600 }}>
                                {data.hrCompliancePercent}%
                            </span>
                            <span style={{ color: "var(--muted)", fontSize: "0.85rem" }}>
                                {data.compliantStaff} of {data.totalActiveStaff} active staff hold every
                                competency their roles require
                            </span>
                        </div>
                        <div className="meter" style={{ marginTop: "0.85rem" }}>
                            <div className="meter-track">
                                <div
                                    className="meter-fill"
                                    style={{
                                        width: `${data.hrCompliancePercent}%`,
                                        background: meterColour(data.hrCompliancePercent),
                                    }}
                                />
                            </div>
                            <span className="meter-value">{data.hrCompliancePercent}%</span>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-head">
                        <h2>Compliance overrides</h2>
                        <span className="chip warn">{data.overriddenShifts.length} flagged</span>
                    </div>
                    <div className="card-body">
                        {data.overriddenShifts.length === 0 ? (
                            <div className="empty" style={{ padding: "0.5rem 0" }}>No shifts have been overridden.</div>
                        ) : data.overriddenShifts.map((shift) => (
                            <div key={shift.id} className="person-row" style={{ alignItems: "flex-start" }}>
                                <span className="mono" style={{
                                    fontWeight: 700, fontSize: "0.68rem", color: "var(--warn)",
                                    background: "var(--warn-bg)", borderRadius: 6,
                                    padding: "0.28rem 0.5rem", flexShrink: 0,
                                }}>
                                    {(shift.override_rule_ids ?? []).join(" ") || "R?"}
                                </span>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{shift.employee_name ?? "—"}</div>
                                    <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: 2 }}>
                                        "{shift.override_reason}"
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="grid cols-3">
                <div className="card">
                    <div className="card-head"><h2>Upcoming shifts</h2></div>
                    <div className="card-body">
                        {data.upcomingShifts.length === 0 ? (
                            <div className="empty">Nothing rostered ahead.</div>
                        ) : data.upcomingShifts.map((shift) => (
                            <div key={shift.id} className="person-row">
                                {/* Same colour language as the compliance lists: the
                                    avatar identifies the person at a glance, and an
                                    unfilled shift wears a neutral "?" instead. */}
                                <InitialsAvatar name={shift.employee_name} />
                                <div className="person-main">
                                    <div className="name">{shift.employee_name ?? "Unassigned"}</div>
                                    <div className="sub">{shift.placement_name}</div>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                    <div className="mono" style={{ fontSize: "0.76rem", whiteSpace: "nowrap" }}>
                                        {formatDateTime(shift.starts_at)}
                                    </div>
                                    {shift.is_overridden && <span className="chip warn">overridden</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="card">
                    <div className="card-head">
                        <h2>Expiring competencies</h2>
                        <span className="chip mute">next 60 days</span>
                    </div>
                    <div className="card-body flush">
                        {data.expiringTraining.length === 0 ? (
                            <div className="empty">Nothing expiring soon.</div>
                        ) : (
                            <div className="table-wrap">
                                <table>
                                    <tbody>
                                        {data.expiringTraining.map((record) => {
                                            const days = daysUntil(record.expiry_date);
                                            return (
                                                <tr key={record.id}>
                                                    <td>
                                                        <div style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
                                                            <InitialsAvatar name={record.employee_name} size={26} />
                                                            <div>
                                                                <div style={{ fontWeight: 600 }}>{record.employee_name}</div>
                                                                <div style={{ color: "var(--muted)", fontSize: "0.75rem" }}>
                                                                    {record.competency_name}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                                        <span className={`chip ${record.already_expired ? "block" : "warn"}`}>
                                                            {record.already_expired
                                                                ? `expired ${Math.abs(days)}d ago`
                                                                : `${days}d`}
                                                        </span>
                                                        <div className="mono" style={{ fontSize: "0.7rem", color: "var(--muted)" }}>
                                                            {formatDate(record.expiry_date)}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                <div className="card">
                    <div className="card-head"><h2>Overdue site tasks</h2></div>
                    <div className="card-body flush">
                        {data.overdueTasks.length === 0 ? (
                            <div className="empty">Nothing overdue.</div>
                        ) : (
                            <div className="table-wrap">
                                <table>
                                    <tbody>
                                        {data.overdueTasks.map((task) => (
                                            <tr key={task.id}>
                                                <td>
                                                    <div style={{ fontWeight: 600 }}>{task.name}</div>
                                                    <div style={{ color: "var(--muted)", fontSize: "0.75rem" }}>
                                                        {task.location_name}
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: "right" }}>
                                                    <span className="chip block">{formatDate(task.due_date)}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="grid cols-2">
                <ComplianceList title="Participants compliance" people={data.participantCompliance} />
                <ComplianceList title="HRM compliance" people={data.staffCompliance} />
            </div>

            {data.openHandovers.length > 0 && (
                <div className="card">
                    <div className="card-head">
                        <h2>Handovers awaiting acknowledgement</h2>
                        <span className="chip warn">{data.openHandovers.length} open</span>
                    </div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr><th>Participant</th><th>Receiving</th><th>Summary</th><th>Issued</th></tr>
                                </thead>
                                <tbody>
                                    {data.openHandovers.map((handover) => (
                                        <tr key={handover.id}>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontWeight: 600 }}>
                                                    <InitialsAvatar name={handover.participant_name} size={24} />
                                                    {handover.participant_name}
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
                                                    <InitialsAvatar name={handover.to_employee_name} size={24} />
                                                    {handover.to_employee_name ?? "—"}
                                                </div>
                                            </td>
                                            <td style={{ color: "var(--ink-soft)" }}>{handover.summary}</td>
                                            <td className="mono">{formatDateTime(handover.occurred_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid cols-3">
                <Feeds items={data.feeds} />
                <PeoplePanel
                    title="Birthdays" icon="gift" people={data.birthdays}
                    describe={(person) => `Birthday on ${formatDate(person.next_date)}`}
                    onWish={setWishTarget}
                    wishSent={alreadyWished}
                />
                <PeoplePanel
                    title="Work anniversary" icon="medal" people={data.anniversaries}
                    describe={(person) =>
                        `${person.years ?? ""} year${person.years === 1 ? "" : "s"} on ${formatDate(person.next_date)}`}
                />
            </div>

            {wishTarget && (
                <WishDialog
                    person={wishTarget}
                    onClose={() => setWishTarget(null)}
                    onSent={() => { setWishTarget(null); sentWishes.reload(); }}
                />
            )}
        </div>
    );
};
